import axios from 'axios';
import { useState, useEffect } from 'react';

/**
 * 消耗活动配置（服务器从游戏配置推导，客户端零修改适配每期活动）。
 * 数据来源：GET /v1/config/consumptionActivity（无需鉴权）
 * 返回结构：{ open, activityId, milestoneActivityId, startTime, endTime,
 *            name, rankName, requiredLv2, lv1ItemName, lv2ItemName, rankItemName,
 *            missions: [{id,type,num,rewardLv1,rewardRank}] }
 */

/** 请求失败/未开放时的默认值（界面按未开启隐藏） */
export const DEFAULT_CONSUMPTION_ACTIVITY = {
    open: false,
    activityId: null,
    milestoneActivityId: null,
    startTime: null,
    endTime: null,
    name: '消耗活动',
    rankName: '龙舟竞渡',
    requiredLv2: 250,
    lv1ItemName: '红枣',
    lv2ItemName: '黄金大枣',
    rankItemName: '芭蕉扇',
    missions: [],
};

/** 缓存有效期（5分钟），活动配置期内不变，无需频繁拉取 */
const CACHE_TTL = 5 * 60 * 1000;
/** 拉取失败后的短重试间隔（15秒），避免一次网络抖动把UI锁死整个TTL */
const FAILURE_TTL = 15 * 1000;

/** 模块级缓存 { data, timestamp, promise }，供 Home/Setting/ConsumptionTaskView 共用 */
const cache = { data: null, timestamp: 0, promise: null };

/**
 * 拉取消耗活动配置（带模块级缓存与并发去重）。
 * @param force 是否忽略缓存强制刷新
 * @returns 配置对象（失败时返回默认值，open=false）
 */
export async function fetchConsumptionActivity(force = false) {
    const now = Date.now();
    if (!force && cache.data && now - cache.timestamp < CACHE_TTL) {
        return cache.data;
    }
    if (!force && cache.promise) {
        return cache.promise;
    }

    cache.promise = (async () => {
        let ok = false;
        try {
            const response = await axios.get('/v1/config/consumptionActivity');
            if (response.data.code === 200 && response.data.body) {
                cache.data = { ...DEFAULT_CONSUMPTION_ACTIVITY, ...response.data.body };
                ok = true;
            } else {
                console.warn('获取消耗活动配置失败:', response.data.msg);
                cache.data = cache.data ?? { ...DEFAULT_CONSUMPTION_ACTIVITY };
            }
        } catch (error) {
            console.error('获取消耗活动配置出错:', error);
            cache.data = cache.data ?? { ...DEFAULT_CONSUMPTION_ACTIVITY };
        } finally {
            // 成功→完整TTL；失败→短TTL(FAILURE_TTL)后重试（令 timestamp 提前过期）
            const nowTs = Date.now();
            cache.timestamp = ok ? nowTs : nowTs - CACHE_TTL + FAILURE_TTL;
            cache.promise = null;
        }
        return cache.data;
    })();

    return cache.promise;
}

/**
 * React hook：获取消耗活动配置。
 * @returns { act, loading } act 为配置对象（初始为默认值）
 */
export function useConsumptionActivity() {
    const [act, setAct] = useState({ ...DEFAULT_CONSUMPTION_ACTIVITY });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        fetchConsumptionActivity().then(data => {
            if (mounted) {
                setAct(data);
                setLoading(false);
            }
        });
        return () => {
            mounted = false;
        };
    }, []);

    return { act, loading };
}
